import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, updateProfile } from 'firebase/auth';
import { auth } from '../../services/firebase';
import { Mail, Lock, Eye, EyeOff, User, AtSign, FileText } from 'lucide-react';

const subtitles = [
  'Where wellness meets personalization',
  'Personalized nutrition tailored to you and your family',
  'Discover recipes that match your dietary needs',
  'Plan meals and track nutrition with care',
];

// ─────────────────────────────────────────────
// Keep in sync with App.jsx
const COMING_SOON = true;
// ─────────────────────────────────────────────

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/';

  // During beta, always lock to login — signup is iOS app only
  const [mode, setMode] = useState(COMING_SOON ? 'login' : (location.pathname === '/signup' ? 'signup' : 'login'));

  const [subtitleIndex, setSubtitleIndex] = useState(0);
  const [subtitleVisible, setSubtitleVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setSubtitleVisible(false);
      setTimeout(() => {
        setSubtitleIndex((i) => (i + 1) % subtitles.length);
        setSubtitleVisible(true);
      }, 400);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setResetSent(false);
  };

  const friendlyError = (err) => {
    const code = err.code || '';
    if (code.includes('invalid-email')) return 'That email address looks invalid.';
    if (code.includes('email-already-in-use')) return 'That email is already in use.';
    if (code.includes('weak-password')) return 'Please use a stronger password (at least 6 characters).';
    if (code.includes('wrong-password') || code.includes('user-not-found') || code.includes('invalid-credential'))
      return 'Incorrect email or password. Please try again.';
    if (code.includes('network-request-failed')) return 'Network error. Please check your connection.';
    if (code.includes('too-many-requests')) return 'Too many attempts. Please try again later.';
    return err.message;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail.trim() || !loginPassword) {
      setError('Please enter both email and password.');
      return;
    }
    setError('');
    try {
      setLoading(true);
      await signInWithEmailAndPassword(auth, loginEmail.trim(), loginPassword);
      navigate(from, { replace: true });
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!loginEmail.trim()) {
      setError('Please enter your email address to reset your password.');
      return;
    }
    setError('');
    try {
      setLoading(true);
      await sendPasswordResetEmail(auth, loginEmail.trim());
      setResetSent(true);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (!firstName || !lastName || !username || !signupEmail || !signupPassword) {
      setError('Please fill in all fields.');
      return;
    }
    if (!acceptedTerms || !acceptedPrivacy) {
      setError('Please accept the legal agreements to continue.');
      return;
    }
    if (signupPassword.length < 6) {
      setError('Please use a stronger password (at least 6 characters).');
      return;
    }
    // Sanitize username: lowercase, strip leading @, allow only alphanumeric + underscore
    const sanitizedUsername = username.trim().replace(/^@/, '').toLowerCase().replace(/[^a-z0-9_]/g, '');
    if (!sanitizedUsername) {
      setError('Username can only contain letters, numbers, and underscores.');
      return;
    }
    try {
      setLoading(true);
      const credential = await createUserWithEmailAndPassword(auth, signupEmail.trim(), signupPassword);
      const displayName = `${firstName.trim()} ${lastName.trim()}`.trim();
      // Set displayName on the Firebase Auth user object immediately
      await updateProfile(credential.user, { displayName });
      navigate('/onboarding', { state: { firstName: firstName.trim(), lastName: lastName.trim(), username: sanitizedUsername } });
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    background: '#fff',
    border: '1px solid rgba(45,95,77,0.2)',
    color: '#2D5F4D',
  };

  const inputClass = 'w-full px-4 py-4 rounded-xl text-base outline-none transition-all placeholder:text-gray-300';

  return (
    <div
      className="min-h-screen w-full"
      style={{
        background: 'linear-gradient(135deg, rgba(45,95,77,0.08) 0%, rgba(139,111,71,0.05) 50%, #F5F3EF 100%)',
      }}
    >
      <div className="max-w-md mx-auto px-8 py-12">

        {mode === 'signup' && (
          <div className="flex flex-col items-center">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mb-6 shadow-lg"
              style={{ background: 'linear-gradient(135deg, #2D5F4D, #3A7A5C)' }}
            >
              <span className="text-white font-bold text-3xl" style={{ fontFamily: 'Georgia, serif' }}>N</span>
            </div>

            <h1
              className="text-4xl font-bold text-center mb-3"
              style={{ fontFamily: 'Georgia, serif', color: '#2D5F4D' }}
            >
              Welcome to Nutrius
            </h1>

            <div className="h-14 flex items-start justify-center mb-8 px-4">
              <p
                className="text-base font-medium text-center leading-relaxed"
                style={{
                  color: 'rgba(139,115,85,0.9)',
                  opacity: subtitleVisible ? 1 : 0,
                  transition: 'opacity 0.4s ease-in-out',
                }}
              >
                {subtitles[subtitleIndex]}
              </p>
            </div>

            {error && (
              <div className="w-full mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200">
                <p className="text-sm text-red-600 text-center">{error}</p>
              </div>
            )}

            <button
              onClick={() => { setError(''); switchMode('signup-form'); }}
              className="w-full py-4 rounded-2xl text-white font-semibold text-base mb-3 transition-all hover:opacity-95 active:scale-99"
              style={{
                background: 'linear-gradient(90deg, #2D5F4D, rgba(45,95,77,0.85))',
                boxShadow: 'rgba(45,95,77,0.3) 0px 8px 24px',
              }}
            >
              Start Your Journey
            </button>

            <button
              onClick={() => switchMode('login')}
              className="w-full py-4 rounded-2xl text-base transition-all hover:bg-white"
              style={{
                color: '#2D5F4D',
                background: 'rgba(255,255,255,0.5)',
                border: '1.5px solid rgba(45,95,77,0.3)',
              }}
            >
              <span className="font-medium">Already have an account? </span>
              <span className="font-semibold">Sign In</span>
            </button>

            <div className="mt-8 text-center">
              <p className="text-xs mb-1" style={{ color: 'rgba(139,115,85,0.7)' }}>
                By continuing, you agree to our
              </p>
              <div className="flex items-center justify-center gap-1">
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-xs font-semibold" style={{ color: '#2D5F4D' }}>Terms</a>
                <span className="text-xs" style={{ color: 'rgba(139,115,85,0.7)' }}> and </span>
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-xs font-semibold" style={{ color: '#2D5F4D' }}>Privacy Policy</a>
              </div>
            </div>
          </div>
        )}

        {mode === 'signup-form' && (
          <div>
            <button
              onClick={() => switchMode('signup')}
              className="mb-6 text-sm font-semibold flex items-center gap-1 transition-opacity hover:opacity-70"
              style={{ color: '#2D5F4D' }}
            >
              ← Back
            </button>

            <h1 className="text-4xl font-bold mb-1" style={{ fontFamily: 'Georgia, serif', color: '#2D5F4D' }}>
              Create Account
            </h1>
            <p className="text-base font-medium mb-8" style={{ color: '#8B7355' }}>
              Let's get you set up
            </p>

            {error && (
              <div className="mb-5 px-4 py-3 rounded-xl bg-red-50 border border-red-200">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <form onSubmit={handleSignup} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#2D5F4D' }}>First Name</label>
                <div className="relative">
                  <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(45,95,77,0.4)' }} />
                  <input type="text" placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required className={inputClass + ' pl-11'} style={inputStyle} onFocus={(e) => (e.target.style.borderColor = 'rgba(45,95,77,0.5)')} onBlur={(e) => (e.target.style.borderColor = 'rgba(45,95,77,0.2)')} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#2D5F4D' }}>Last Name</label>
                <div className="relative">
                  <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(45,95,77,0.4)' }} />
                  <input type="text" placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} required className={inputClass + ' pl-11'} style={inputStyle} onFocus={(e) => (e.target.style.borderColor = 'rgba(45,95,77,0.5)')} onBlur={(e) => (e.target.style.borderColor = 'rgba(45,95,77,0.2)')} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#2D5F4D' }}>Username</label>
                <div className="relative">
                  <AtSign size={18} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(45,95,77,0.4)' }} />
                  <input type="text" placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)} required className={inputClass + ' pl-11'} style={inputStyle} onFocus={(e) => (e.target.style.borderColor = 'rgba(45,95,77,0.5)')} onBlur={(e) => (e.target.style.borderColor = 'rgba(45,95,77,0.2)')} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#2D5F4D' }}>Email</label>
                <div className="relative">
                  <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(45,95,77,0.4)' }} />
                  <input type="email" placeholder="your@email.com" value={signupEmail} onChange={(e) => setSignupEmail(e.target.value)} required className={inputClass + ' pl-11'} style={inputStyle} onFocus={(e) => (e.target.style.borderColor = 'rgba(45,95,77,0.5)')} onBlur={(e) => (e.target.style.borderColor = 'rgba(45,95,77,0.2)')} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#2D5F4D' }}>Password</label>
                <div className="relative">
                  <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(45,95,77,0.4)' }} />
                  <input type={showSignupPassword ? 'text' : 'password'} placeholder="At least 6 characters" value={signupPassword} onChange={(e) => setSignupPassword(e.target.value)} required className={inputClass + ' pl-11 pr-11'} style={inputStyle} onFocus={(e) => (e.target.style.borderColor = 'rgba(45,95,77,0.5)')} onBlur={(e) => (e.target.style.borderColor = 'rgba(45,95,77,0.2)')} />
                  <button type="button" onClick={() => setShowSignupPassword(!showSignupPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 transition-opacity hover:opacity-70" style={{ color: 'rgba(45,95,77,0.5)' }}>
                    {showSignupPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="pt-4 border-t" style={{ borderColor: 'rgba(45,95,77,0.1)' }}>
                <div className="flex items-center gap-2 mb-1">
                  <FileText size={16} style={{ color: '#2D5F4D' }} />
                  <span className="text-sm font-semibold" style={{ color: '#2D5F4D' }}>Legal Agreements</span>
                </div>
                <p className="text-xs mb-3" style={{ color: '#8B7355' }}>Please review and accept to continue</p>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 rounded-xl cursor-pointer" style={{ background: 'rgba(255,255,255,0.6)' }}>
                    <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="w-4 h-4 rounded accent-emerald-800" />
                    <span className="text-sm" style={{ color: '#2D5F4D' }}>I accept the <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline font-semibold" style={{ color: '#2D5F4D' }}>Terms of Service</a></span>
                  </label>
                  <label className="flex items-center gap-3 p-3 rounded-xl cursor-pointer" style={{ background: 'rgba(255,255,255,0.6)' }}>
                    <input type="checkbox" checked={acceptedPrivacy} onChange={(e) => setAcceptedPrivacy(e.target.checked)} className="w-4 h-4 rounded accent-emerald-800" />
                    <span className="text-sm" style={{ color: '#2D5F4D' }}>I accept the <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline font-semibold" style={{ color: '#2D5F4D' }}>Privacy Policy</a></span>
                  </label>
                </div>
              </div>

              <button type="submit" disabled={loading || !acceptedTerms || !acceptedPrivacy} className="w-full py-4 rounded-2xl text-white font-semibold text-base transition-all hover:opacity-95 active:scale-99 disabled:opacity-50 disabled:cursor-not-allowed mt-2" style={{ background: 'linear-gradient(90deg, #2D5F4D, rgba(45,95,77,0.85))', boxShadow: 'rgba(45,95,77,0.3) 0px 8px 24px' }}>
                {loading ? 'Creating Account...' : 'Continue'}
              </button>

              <p className="text-center text-sm pt-1" style={{ color: '#8B7355' }}>
                Already have an account?{' '}
                <button type="button" onClick={() => switchMode('login')} className="font-semibold underline" style={{ color: '#2D5F4D' }}>Sign in</button>
              </p>
            </form>
          </div>
        )}

        {mode === 'login' && (
          <div>
            <div className="flex flex-col items-center mb-8">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-5 shadow-md" style={{ background: 'linear-gradient(135deg, #2D5F4D, #3A7A5C)' }}>
                <span className="text-white font-bold text-2xl" style={{ fontFamily: 'Georgia, serif' }}>N</span>
              </div>
              <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Georgia, serif', color: '#2D5F4D' }}>Welcome Back</h1>
              <p className="text-base font-medium" style={{ color: 'rgba(139,115,85,0.8)' }}>Sign in to continue</p>
            </div>

            {resetSent && (
              <div className="mb-5 px-4 py-3 rounded-xl bg-green-50 border border-green-200">
                <p className="text-sm text-green-700 text-center">Password reset link sent to {loginEmail}. Check your inbox.</p>
              </div>
            )}

            {error && (
              <div className="mb-5 px-4 py-3 rounded-xl bg-red-50 border border-red-200">
                <p className="text-sm text-red-600 text-center">{error}</p>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#2D5F4D' }}>Email</label>
                <input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="your@email.com" required className={inputClass} style={inputStyle} onFocus={(e) => (e.target.style.borderColor = 'rgba(45,95,77,0.5)')} onBlur={(e) => (e.target.style.borderColor = 'rgba(45,95,77,0.2)')} />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold" style={{ color: '#2D5F4D' }}>Password</label>
                  <button type="button" onClick={handleForgotPassword} disabled={loading} className="text-sm font-semibold transition-opacity hover:opacity-70 disabled:opacity-40" style={{ color: 'rgba(45,95,77,0.7)' }}>Forgot?</button>
                </div>
                <div className="relative">
                  <input type={showLoginPassword ? 'text' : 'password'} value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} placeholder="••••••••" required className={inputClass + ' pr-11'} style={inputStyle} onFocus={(e) => (e.target.style.borderColor = 'rgba(45,95,77,0.5)')} onBlur={(e) => (e.target.style.borderColor = 'rgba(45,95,77,0.2)')} />
                  <button type="button" onClick={() => setShowLoginPassword(!showLoginPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 transition-opacity hover:opacity-70" style={{ color: 'rgba(45,95,77,0.5)' }}>
                    {showLoginPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading} className="w-full py-4 rounded-2xl text-white font-semibold text-base transition-all hover:opacity-95 active:scale-99 disabled:opacity-50 disabled:cursor-not-allowed mt-2" style={{ background: 'linear-gradient(90deg, #2D5F4D, rgba(45,95,77,0.85))', boxShadow: 'rgba(45,95,77,0.3) 0px 8px 24px' }}>
                {loading ? 'Signing In...' : 'Sign In'}
              </button>

              {!COMING_SOON && (
                <button type="button" onClick={() => switchMode('signup')} className="w-full py-4 rounded-2xl text-base transition-all hover:bg-white" style={{ color: '#2D5F4D', background: 'rgba(255,255,255,0.5)', border: '1.5px solid rgba(45,95,77,0.3)' }}>
                  <span className="font-medium">Don't have an account? </span>
                  <span className="font-semibold">Sign Up</span>
                </button>
              )}

              {COMING_SOON && (
                <div className="w-full py-4 px-5 rounded-2xl text-center" style={{ background: 'rgba(45,95,77,0.05)', border: '1.5px solid rgba(45,95,77,0.15)' }}>
                  <p className="text-sm font-medium" style={{ color: 'rgba(45,95,77,0.7)' }}>
                    New accounts are created via the{' '}
                    <a href="/beta" className="font-semibold underline" style={{ color: '#2D5F4D' }}>iOS beta app</a>
                  </p>
                </div>
              )}
            </form>
          </div>
        )}

      </div>
    </div>
  );
};

export default AuthPage;